import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["pages", "error", "previous", "next", "pageStatus", "mark"]
  static values = { url: String, workerUrl: String, continuous: Boolean }

  async connect() {
    this.currentPageNumber = 1
    this.pageCount = 0
    this.rendering = false
    this.pageMetrics = new Map()
    this.observedPages = new Set()
    this.resizeObserver = new ResizeObserver((entries) => {
      entries.forEach(({target}) => this.sizeMarks(target))
    })

    try {
      const pdfjs = await import("pdfjs-dist")
      pdfjs.GlobalWorkerOptions.workerSrc = this.workerUrlValue
      this.pdf = await pdfjs.getDocument({url: this.urlValue, withCredentials: true}).promise
      this.pageCount = this.pdf.numPages

      if (this.continuousValue) {
        await this.showAllPages()
      } else {
        if (this.hasPageStatusTarget) this.pageStatusTarget.textContent = `Page 1 of ${this.pageCount}`
        await this.showPage(1)
      }
    } catch (error) {
      console.error("Failed to render signing document:", error)
      this.pagesTarget.classList.add("hidden")
      this.errorTarget.classList.remove("hidden")
      if (this.hasPageStatusTarget) this.pageStatusTarget.textContent = "PDF preview unavailable."
      if (this.hasPreviousTarget) this.previousTarget.disabled = true
      if (this.hasNextTarget) this.nextTarget.disabled = true
      this.dispatchPageChange({unavailable: true})
    }
  }

  disconnect() {
    this.resizeObserver.disconnect()
  }

  markTargetConnected(mark) {
    const pageElement = mark.closest("[data-signing-document-page-content]")
    if (this.pageMetrics && pageElement) this.sizeMark(mark, pageElement)
  }

  preserveMarkDimensions(event) {
    if (event.detail.attributeName === "style" && event.detail.mutationType === "remove" &&
      event.target.matches("[data-signing-document-target~='mark']")) event.preventDefault()
  }

  async showPreviousPage() {
    await this.showPage(this.currentPageNumber - 1)
  }

  async showNextPage() {
    await this.showPage(this.currentPageNumber + 1)
  }

  async showAllPages() {
    if (!this.pdf || this.rendering) return

    this.rendering = true
    try {
      for (let pageNumber = 1; pageNumber <= this.pageCount; pageNumber += 1) {
        const page = await this.pdf.getPage(pageNumber)
        const wrapper = await this.renderPage(page, pageNumber)
        wrapper.classList.remove("hidden")
        wrapper.classList.add("flex")
        this.pagesTarget.querySelector("[data-signing-document-loading]")?.remove()
        this.sizeMarks(wrapper.querySelector("[data-signing-document-page-content]"))
      }
    } finally {
      this.rendering = false
    }
  }

  async showPage(pageNumber) {
    if (!this.pdf || this.rendering || pageNumber < 1 || pageNumber > this.pageCount) return

    this.rendering = true
    this.updateControls()

    try {
      const page = await this.pdf.getPage(pageNumber)
      const pageElement = await this.renderPage(page, pageNumber)
      this.pagesTarget.querySelector("[data-signing-document-loading]")?.remove()

      for (const persistedPage of this.pagesTarget.querySelectorAll("[data-signing-document-page-number]")) {
        const active = persistedPage === pageElement
        persistedPage.classList.toggle("hidden", !active)
        persistedPage.classList.toggle("flex", active)
      }

      this.sizeMarks(pageElement.querySelector("[data-signing-document-page-content]"))
      this.currentPageNumber = pageNumber
      if (this.hasPageStatusTarget) this.pageStatusTarget.textContent = `Page ${pageNumber} of ${this.pageCount}`
    } finally {
      this.rendering = false
      this.updateControls()
      this.dispatchPageChange()
    }
  }

  async renderPage(page, pageNumber) {
    const wrapper = this.pagesTarget.querySelector(`[data-signing-document-page-number="${pageNumber}"]`)
    const pageElement = wrapper.querySelector("[data-signing-document-page-content]")
    const canvasContainer = pageElement.querySelector("[data-signing-document-canvas]")

    if (canvasContainer.hasChildNodes()) return wrapper

    const unscaledViewport = page.getViewport({scale: 1})
    const viewport = page.getViewport({scale: 2})
    this.pageMetrics.set(pageNumber, {width: unscaledViewport.width, height: unscaledViewport.height})
    pageElement.style.width = "100%"
    if (!this.observedPages.has(pageElement)) {
      this.resizeObserver.observe(pageElement)
      this.observedPages.add(pageElement)
    }

    const canvas = document.createElement("canvas")
    canvas.width = viewport.width
    canvas.height = viewport.height
    canvas.className = "block h-auto w-full"
    await page.render({canvasContext: canvas.getContext("2d"), viewport}).promise
    canvasContainer.replaceChildren(canvas)
    return wrapper
  }

  sizeMarks(pageElement) {
    const wrapper = pageElement.closest("[data-signing-document-page-number]")
    const metrics = this.pageMetrics.get(Number(wrapper.dataset.signingDocumentPageNumber))
    if (!metrics || !pageElement.clientWidth || !pageElement.clientHeight) return

    pageElement.querySelectorAll("[data-signing-document-target~='mark']").forEach((mark) => {
      this.sizeMark(mark, pageElement, metrics)
    })
  }

  sizeMark(mark, pageElement, metrics = null) {
    const wrapper = pageElement.closest("[data-signing-document-page-number]")
    metrics ||= this.pageMetrics.get(Number(wrapper.dataset.signingDocumentPageNumber))
    if (!metrics || !pageElement.clientWidth || !pageElement.clientHeight) return

    if (!mark.complete || !mark.naturalWidth || !mark.naturalHeight) {
      mark.addEventListener("load", () => this.sizeMark(mark, pageElement, metrics), {once: true})
      return
    }

    const placement = mark.closest("[data-signing-placement-applied-value], [data-signing-mark-applied]")
    const boxWidth = pageElement.clientWidth * Number(placement.dataset.placementWidth)
    const boxHeight = pageElement.clientHeight * Number(placement.dataset.placementHeight)
    const maxHeight = Number(placement.dataset.placementMaxMarkHeight) * pageElement.clientHeight / metrics.height
    const scale = Math.min(boxWidth / mark.naturalWidth, boxHeight / mark.naturalHeight, maxHeight / mark.naturalHeight)

    mark.style.width = `${mark.naturalWidth * scale}px`
    mark.style.height = `${mark.naturalHeight * scale}px`
  }

  dispatchPageChange({unavailable = false} = {}) {
    this.dispatch("page-change", {
      bubbles: true,
      detail: {
        pageNumber: this.currentPageNumber,
        pageCount: this.pageCount,
        rendering: this.rendering,
        unavailable
      }
    })
  }

  updateControls() {
    if (this.hasPreviousTarget) this.previousTarget.disabled = this.rendering || this.currentPageNumber <= 1
    if (this.hasNextTarget) this.nextTarget.disabled = this.rendering || this.currentPageNumber >= this.pageCount
  }
}
